import { useState, useEffect, useCallback } from 'react';
import { ShieldAlert, Landmark, Gift, AlertTriangle, Info } from 'lucide-react';
import axios from 'axios';
import WorkforcePellCallout from './WorkforcePellCallout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

/**
 * FederalAidMeter, visual gauge showing, for a given cost of attendance, how
 * much is covered by federal student loans (capped per OBBBA), estimated
 * Pell/grants, and the remaining gap a family must cover. Pulls all numbers
 * from /api/federal-aid/eligibility (single source of truth) so it renders
 * identically for students and for parents viewing a child.
 *
 * Props:
 *   totalCost, annualCost, years, borrowerType, dependencyStatus,
 *   yearInProgram, estimatedAnnualAid, programStartDate
 */
export default function FederalAidMeter({
  totalCost = 0,
  annualCost = 0,
  years = 4,
  borrowerType = 'undergrad',
  dependencyStatus = 'dependent',
  yearInProgram = 1,
  estimatedAnnualAid = 0,
  programStartDate = null,
  showWorkforceCallout = true,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/federal-aid/eligibility`, {
        total_cost: totalCost,
        annual_cost: annualCost,
        years,
        borrower_type: borrowerType,
        dependency_status: dependencyStatus,
        year_in_program: yearInProgram,
        estimated_annual_aid: estimatedAnnualAid,
        program_start_date: programStartDate,
      });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [totalCost, annualCost, years, borrowerType, dependencyStatus, yearInProgram, estimatedAnnualAid, programStartDate]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="p-5 rounded-xl border border-slate-700/50 bg-slate-800/30 animate-pulse h-40" data-testid="federal-aid-meter-loading" />
    );
  }

  const segs = [
    { key: 'federal_loans', label: 'Federal loans', icon: Landmark, color: 'bg-blue-500', text: 'text-blue-300', ...data.annual.federal_loans },
    { key: 'grants', label: 'Pell / grants', icon: Gift, color: 'bg-emerald-500', text: 'text-emerald-300', ...data.annual.grants },
    { key: 'gap', label: 'Gap to cover', icon: AlertTriangle, color: 'bg-rose-500', text: 'text-rose-300', ...data.annual.gap },
  ];

  return (
    <div
      className="p-5 rounded-xl border border-blue-500/25 bg-gradient-to-br from-slate-900/60 to-blue-950/30 backdrop-blur-xl space-y-4"
      data-testid="federal-aid-meter"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-blue-500/20 ring-1 ring-blue-400/30 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-blue-300" />
          </div>
          <div>
            <h4 className="text-white font-semibold leading-tight">Federal Aid Eligibility Meter</h4>
            <p className="text-slate-300 text-xs">
              {data.is_obbba ? 'One Big Beautiful Bill Act rules' : data.rules_label} · per year
            </p>
          </div>
        </div>
        {data.is_obbba && (
          <span className="text-[10px] uppercase tracking-wide px-2 py-1 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 whitespace-nowrap">
            OBBBA 2026
          </span>
        )}
      </div>

      {/* Stacked bar (annual basis) */}
      <div>
        <div className="flex justify-between text-xs text-slate-300 mb-1.5">
          <span>Annual cost of attendance</span>
          <span className="text-white font-semibold">{fmt(data.annual.cost)}/yr</span>
        </div>
        <div className="flex w-full h-5 rounded-full overflow-hidden bg-slate-800 ring-1 ring-slate-700" data-testid="federal-aid-meter-bar">
          {segs.map((s) => s.pct > 0 && (
            <div
              key={s.key}
              className={`${s.color} h-full transition-all`}
              style={{ width: `${s.pct}%` }}
              title={`${s.label}: ${fmt(s.amount)}`}
              data-testid={`meter-seg-${s.key}`}
            />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3">
          {segs.map((s) => (
            <div key={s.key} className="rounded-lg bg-slate-800/50 border border-slate-700/50 p-2.5" data-testid={`meter-stat-${s.key}`}>
              <div className="flex items-center gap-1.5">
                <span className={`w-2.5 h-2.5 rounded-full ${s.color}`} />
                <span className="text-[11px] text-slate-300">{s.label}</span>
              </div>
              <p className="text-white font-bold text-sm mt-0.5">{fmt(s.amount)}</p>
              <p className={`text-[10px] ${s.text}`}>{s.pct}% of cost</p>
            </div>
          ))}
        </div>
      </div>

      {/* Plain-language OBBBA reminders */}
      {(data.messages || []).length > 0 && (
        <div className="space-y-1.5 pt-1">
          {data.messages.map((m, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-slate-300" data-testid={`meter-message-${i}`}>
              <Info className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
              <span>{m}</span>
            </div>
          ))}
        </div>
      )}

      {/* 4-year gap summary */}
      {data.total?.gap?.amount > 0 && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-rose-500/10 border border-rose-500/25" data-testid="meter-total-gap">
          <span className="text-rose-200 text-sm font-medium">Estimated {data.years}-year gap to cover</span>
          <span className="text-rose-100 font-bold text-lg">{fmt(data.total.gap.amount)}</span>
        </div>
      )}

      {showWorkforceCallout && <WorkforcePellCallout compact />}

      <p className="text-[10px] text-slate-300 leading-relaxed">
        Estimates only, not financial or legal advice. Federal caps, rates, and aid packages vary; confirm with the
        school&apos;s financial aid office.
      </p>
    </div>
  );
}
